import React, { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTheme, alpha } from "@mui/material";
import ReactMarkdown from 'react-markdown'

interface CollapsibleMessageBoxProps {
    title?: string;
    message: string;
    variant?: "success" | "error" | "info" | "warning";
    borderRadius?: string;
    maxHeightVh?: number;
}

export function CollapsibleMessageBox({
    title,
    message,
    variant = "info",
    borderRadius = "16px",
    maxHeightVh = 30,
}: CollapsibleMessageBoxProps) {
    const theme = useTheme();
    const contentRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [needsExpansion, setNeedsExpansion] = useState(false);

    // Check if content overflows the max height
    useEffect(() => {
        const checkOverflow = () => {
            const maxHeight = (window.innerHeight * maxHeightVh) / 100;
            const contentHeight = contentRef.current?.scrollHeight || 0;
            setNeedsExpansion(contentHeight > maxHeight);
        };
        
        // Check on mount and when content changes
        checkOverflow();
        
        // Also check on window resize
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [message, maxHeightVh]);

    // Get colors based on variant
    const getColors = () => {
        switch (variant) {
            case "success":
                return {
                    bg: alpha(theme.palette.success.light, 0.15),
                    border: alpha(theme.palette.success.main, 0.2),
                    text: theme.palette.success.main,
                    buttonColor: theme.palette.success.dark,
                    buttonHoverBg: alpha(theme.palette.success.main, 0.15),
                };
            case "error":
                return {
                    bg: alpha(theme.palette.error.light, 0.15),
                    border: alpha(theme.palette.error.main, 0.2),
                    text: theme.palette.error.main,
                    buttonColor: theme.palette.error.dark,
                    buttonHoverBg: alpha(theme.palette.error.main, 0.15),
                };
            case "warning":
                return {
                    bg: alpha(theme.palette.warning.light, 0.15),
                    border: alpha(theme.palette.warning.main, 0.2),
                    text: theme.palette.warning.main,
                    buttonColor: theme.palette.warning.dark,
                    buttonHoverBg: alpha(theme.palette.warning.main, 0.15),
                };
            case "info":
            default:
                return {
                    bg: alpha(theme.palette.info.light, 0.15),
                    border: alpha(theme.palette.info.main, 0.2),
                    text: theme.palette.info.main,
                    buttonColor: theme.palette.info.dark,
                    buttonHoverBg: alpha(theme.palette.info.main, 0.15),
                };
        }
    };

    const colors = getColors();

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius,
                bgcolor: colors.bg,
                border: `1px solid ${colors.border}`,
                overflow: 'hidden',
            }}
        >
            <Box
                ref={contentRef}
                sx={{
                    p: 2,
                    maxHeight: needsExpansion && !isExpanded ? `${maxHeightVh}vh` : 'none',
                    overflow: needsExpansion && !isExpanded ? 'hidden' : 'visible',
                    transition: 'max-height 0.3s ease',
                }}
            >
                {title && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: colors.text,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            display: 'block',
                            mb: 1,
                        }}
                    >
                        {title}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    component="div"
                    sx={{
                        color: variant === "error" ? colors.text : theme.palette.text.primary,
                        lineHeight: 1.7,
                        '& p': { mt: 0, mb: 1.5, '&:last-child': { mb: 0 } },
                        '& strong': { fontWeight: 700 },
                        '& ul, & ol': { mt: 0, mb: 1.5, pl: 3 },
                        '& li': { mb: 0.5 },
                        '& code': { 
                            bgcolor: alpha(colors.text, 0.1), 
                            px: 0.6, py: 0.2, 
                            borderRadius: 1, 
                            fontFamily: 'monospace',
                            fontSize: '0.85em'
                        },
                        '& pre': {
                            bgcolor: alpha(colors.text, 0.05),
                            p: 1.5,
                            borderRadius: 2,
                            overflowX: 'auto',
                            '& code': { bgcolor: 'transparent', p: 0 }
                        },
                        '& a': {
                            color: colors.text,
                            textDecoration: 'underline',
                            fontWeight: 600,
                            '&:hover': {
                                opacity: 0.8,
                            },
                        },
                    }}
                >
                    <ReactMarkdown>{message}</ReactMarkdown>
                </Typography>
            </Box>
            
            {/* Show All / Show Less Button */}
            {needsExpansion && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 1,
                        px: 2,
                        bgcolor: colors.bg,
                        borderTop: `1px solid ${colors.border}`,
                    }}
                >
                    <Button
                        size="small"
                        onClick={() => setIsExpanded(!isExpanded)}
                        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{
                            textTransform: 'none',
                            color: colors.buttonColor,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: colors.buttonHoverBg,
                            },
                        }}
                    >
                        {isExpanded ? 'Show Less' : 'Show All'}
                    </Button>
                </Box>
            )}
        </Paper>
    );
}
